import type { VehicleStrategy, VehiclePhysicsConfig } from './VehicleStrategy'

export class MotorcycleStrategy implements VehicleStrategy {
  private readonly config: VehiclePhysicsConfig = {
    productiveGForce: 0.73,
    hardBrakingThreshold: -6.0,
    hardAccelerationThreshold: 3.0,
    canLean: true,
    tractionLimitLeanAngle: 42,
    minimumCorneringG: 0.6,
    engineBrakeThreshold: 3.0,
    moderateBrakingThreshold: -3.0,
    moderateAccelerationThreshold: 1.5,
  }

  public getVehicleType = (): string => {
    return 'motorcycle'
  }

  public getPhysicsConfig = (): VehiclePhysicsConfig => {
    return this.config
  }

  public calculateCoastingIntensity = (
    acceleration: number,
    leanAngle: number,
    lateralG: number,
    combinedG: number,
    speed: number
  ): number => {
    const absLeanAngle = Math.abs(leanAngle)
    const tractionLimit = this.config.tractionLimitLeanAngle ?? 42

    // At very high lean angles (>42°), you're at traction limit
    // Any riding at this angle is productive, not coasting
    if (absLeanAngle > tractionLimit) {
      return 0
    }

    // Check combined g-force early - if you're above threshold, it's productive riding
    // This catches cases where light braking at high lean = high combined g
    if (combinedG >= this.config.productiveGForce) {
      return 0
    }

    // HIGH SPEED COMPENSATION: At high speeds, aerodynamic drag limits acceleration
    // At 250+ km/h, even full throttle might only produce 1-2 m/s² acceleration
    // This prevents false coasting detection on high-speed straights
    if (this.isHighSpeedFullThrottle(speed, acceleration)) {
      return 0
    }

    // Very hard braking (>6 m/s² or ~0.6g) - definitely productive
    if (acceleration < this.config.hardBrakingThreshold) {
      return 0
    }

    const isAccelerating = acceleration > 0
    const isBraking = acceleration < 0
    const absAcceleration = Math.abs(acceleration)

    // BRAKING LOGIC: Motorcycles can all brake similarly hard
    if (isBraking) {
      return this.calculateBrakingCoastingIntensity(
        absAcceleration,
        absLeanAngle
      )
    }

    // ACCELERATION LOGIC: Vehicle-dependent, be more lenient
    if (isAccelerating) {
      const accelIntensity =
        this.calculateAccelerationCoastingIntensity(absAcceleration)
      if (accelIntensity !== null) {
        return accelIntensity
      }
      // Fall through to combined g-force logic for low acceleration
    }

    // Neutral (neither accelerating nor braking significantly) OR
    // Low acceleration (<1.5 m/s²)
    // Use combined g-force to determine coasting
    return this.calculateCombinedGForceCoastingIntensity(combinedG)
  }

  private readonly calculateBrakingCoastingIntensity = (
    absAcceleration: number,
    absLeanAngle: number
  ): number => {
    const HIGH_LEAN_FOR_BRAKING = 35
    const MODERATE_LEAN_FOR_BRAKING = 20

    if (absAcceleration < this.config.engineBrakeThreshold) {
      // Light braking or just engine braking
      // Engine braking is valid even at maximum lean
      // Only flag as coasting if at low/moderate lean where you should brake harder

      if (absLeanAngle < HIGH_LEAN_FOR_BRAKING) {
        // Low/moderate lean - should be braking harder than just engine brake
        // Scale intensity based on how much you're braking
        // 0 m/s² = 85% coasting, 3 m/s² (engine brake) = 45% coasting
        const intensity =
          0.85 - (absAcceleration / this.config.engineBrakeThreshold) * 0.4
        return Math.max(0.45, intensity)
      }
      // Very high lean (35°+) - engine braking is appropriate, not coasting
      return 0
    } else if (absAcceleration <= 6.0) {
      // Moderate braking (3-6 m/s²) - harder than engine brake but not max braking
      if (absLeanAngle < MODERATE_LEAN_FOR_BRAKING) {
        // Low/moderate lean - you can brake much harder
        // Scale: 3 m/s² = 45% coasting, 6 m/s² = 10% coasting
        const intensity = 0.45 - ((absAcceleration - 3.0) / 3.0) * 0.35
        return Math.max(0.1, intensity)
      }
      // High lean (20°+) - moderate braking is reasonable
    }

    return 0
  }

  private readonly calculateAccelerationCoastingIntensity = (
    absAcceleration: number
  ): number | null => {
    if (absAcceleration >= this.config.hardAccelerationThreshold) {
      // Good acceleration (>3 m/s²) - definitely productive, likely full throttle
      return 0
    } else if (absAcceleration >= this.config.moderateAccelerationThreshold) {
      // Moderate acceleration (1.5-3 m/s²) - could be full throttle at very high speeds
      // Be very lenient to avoid penalizing riders at maximum performance
      // Scale: 3 m/s² = 0%, 1.5 m/s² = 15%
      const intensity =
        ((3.0 - absAcceleration) / this.config.moderateAccelerationThreshold) *
        0.15
      return Math.max(0, intensity)
    }

    // Low acceleration (<1.5 m/s²) - use combined g-force logic
    return null
  }

  private readonly calculateCombinedGForceCoastingIntensity = (
    combinedG: number
  ): number => {
    let intensity: number

    if (combinedG >= this.config.productiveGForce) {
      // >0.73g combined - productive riding, pushing hard
      intensity = 0
    } else if (combinedG >= 0.5) {
      // 0.5-0.73g combined - moderate effort
      // Linear scale: 0.73g = 0%, 0.5g = 40%
      intensity = ((this.config.productiveGForce - combinedG) / 0.23) * 0.4
    } else if (combinedG >= 0.3) {
      // 0.3-0.5g combined - light effort, starting to coast
      // Linear scale: 0.5g = 40%, 0.3g = 70%
      intensity = 0.4 + ((0.5 - combinedG) / 0.2) * 0.3
    } else {
      // <0.3g combined - definitely coasting
      // Scale: 0.3g = 70%, 0g = 100%
      intensity = 0.7 + ((0.3 - combinedG) / 0.3) * 0.3
    }

    // Clamp to valid range [0, 1]
    return Math.max(0, Math.min(1, intensity))
  }

  /**
   * Estimates maximum possible acceleration at a given speed based on physics.
   *
   * Physics model:
   * - Power available to accelerate = Engine power - Drag power
   * - Drag power = 0.5 × ρ × Cd × A × v³ (cubic relationship with speed)
   * - Force available = Power / velocity
   * - Acceleration = Force / mass
   *
   * At low speeds: Traction/power limited (5-8 m/s²)
   * At high speeds: Drag limited (approaches zero as speed → v_max)
   *
   * This model allows us to determine if observed acceleration is close to the
   * theoretical maximum, indicating full throttle.
   *
   * @param speedKmh - Current speed in km/h
   * @returns Estimated maximum acceleration in m/s² at this speed
   */
  private readonly estimateMaxAccelerationAtSpeed = (
    speedKmh: number
  ): number => {
    const speedMs = speedKmh / 3.6

    // Physical parameters for typical 1000cc sportbike
    const MAX_LOW_SPEED_ACCEL = 6.0 // m/s² - traction/wheelie limited at low speed
    const TOP_SPEED_MS = 300 / 3.6 // ~300 km/h in m/s (typical top speed)

    // Speed factor: how close are we to top speed?
    // At v=0: factor = 1.0 (full acceleration available)
    // At v=top_speed: factor = 0 (no acceleration available)
    // Using cubic relationship to match drag power curve
    const speedFactor = 1.0 - Math.pow(speedMs / TOP_SPEED_MS, 3)

    // Maximum acceleration degrades with cube of speed ratio
    // This matches the physics: Power_available = Power_engine × (1 - (v/v_max)³)
    const maxAccel = MAX_LOW_SPEED_ACCEL * Math.max(0, speedFactor)

    return maxAccel
  }

  /**
   * Detects if the rider is at full throttle based on speed-dependent acceleration limits.
   *
   * Instead of hard-coded thresholds, this uses a physics-based model that estimates
   * maximum possible acceleration at each speed. If observed acceleration is close to
   * (or exceeds) the theoretical maximum, we conclude the rider is at full throttle.
   *
   * @param speed - Current speed in km/h
   * @param acceleration - Current acceleration in m/s²
   * @returns true if this is likely full throttle for the given speed
   */
  private readonly isHighSpeedFullThrottle = (
    speed: number,
    acceleration: number
  ): boolean => {
    // Only applies to positive acceleration (throttle application)
    if (acceleration <= 0) {
      return false
    }

    // Estimate maximum possible acceleration at current speed
    const maxAccelAtSpeed = this.estimateMaxAccelerationAtSpeed(speed)

    // If observed acceleration is at least 60% of theoretical maximum,
    // consider it full throttle (allows for GPS noise, rider not quite WOT, etc.)
    const THROTTLE_THRESHOLD = 0.6

    // If we're accelerating at ≥60% of max possible, that's full throttle
    return acceleration >= maxAccelAtSpeed * THROTTLE_THRESHOLD
  }
}
